import React, { useCallback, useMemo, useState, createElement } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, DownloadIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, FileSpreadsheetIcon } from 'lucide-react';
const TEMPLATE_COLUMNS = [{
  key: 'supplierName',
  label: 'Supplier Name',
  hint: 'Company or individual name',
  required: true
}, {
  key: 'supplierEmail',
  label: 'Supplier Email',
  hint: 'Contact email',
  required: true
}, {
  key: 'billNumber',
  label: 'Bill Number',
  hint: 'e.g. INV-2026-001',
  required: true
}, {
  key: 'amount',
  label: 'Amount',
  hint: 'Numeric value',
  required: true
}, {
  key: 'currency',
  label: 'Currency',
  hint: `e.g. ${getTenant().currency.code}`,
  required: false
}, {
  key: 'dueDate',
  label: 'Due Date',
  hint: 'YYYY-MM-DD',
  required: true
}, {
  key: 'issueDate',
  label: 'Issue Date',
  hint: 'YYYY-MM-DD',
  required: false
}, {
  key: 'description',
  label: 'Description',
  hint: 'Bill description',
  required: false
}, {
  key: 'itemDescription',
  label: 'Item Description',
  hint: 'Line item name',
  required: false
}, {
  key: 'itemQuantity',
  label: 'Item Quantity',
  hint: 'Number',
  required: false
}, {
  key: 'itemUnitPrice',
  label: 'Item Unit Price',
  hint: 'Numeric value',
  required: false
}, {
  key: 'notes',
  label: 'Notes',
  hint: 'Additional notes',
  required: false
}];
interface ParsedRow {
  id: string;
  data: Record<string, string>;
  errors: string[];
}
function generateCSVTemplate(): string {
  const header = TEMPLATE_COLUMNS.map(c => c.label).join(',');
  const sampleRow = ['Acme Corp Ltd', 'billing@acmecorp.com', 'INV-2026-020', '45000', getTenant().currency.code, '2026-04-15', '2026-03-15', 'Monthly service fee', 'Consulting services', '1', '45000', 'Net 30 payment terms'].join(',');
  return `${header}\n${sampleRow}`;
}
function parseCSV(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return {
    headers: [],
    rows: []
  };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
  return {
    headers,
    rows
  };
}
function mapHeaderToKey(header: string): string | null {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const col of TEMPLATE_COLUMNS) {
    const colNorm = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === colNorm) return col.key;
    const keyNorm = col.key.toLowerCase();
    if (normalized === keyNorm) return col.key;
  }
  return null;
}
function validateRow(data: Record<string, string>): string[] {
  const errors: string[] = [];
  if (!data.supplierName) errors.push('Supplier Name is required');
  if (!data.supplierEmail) errors.push('Supplier Email is required');
  if (data.supplierEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.supplierEmail)) {
    errors.push('Invalid email format');
  }
  if (!data.billNumber) errors.push('Bill Number is required');
  if (!data.amount) {
    errors.push('Amount is required');
  } else if (isNaN(Number(data.amount)) || Number(data.amount) <= 0) {
    errors.push('Amount must be a positive number');
  }
  if (!data.dueDate) {
    errors.push('Due Date is required');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(data.dueDate)) {
    errors.push('Due Date must be in YYYY-MM-DD format');
  }
  if (data.issueDate && !/^\d{4}-\d{2}-\d{2}$/.test(data.issueDate)) {
    errors.push('Issue Date must be in YYYY-MM-DD format');
  }
  return errors;
}
export function BulkBillUpload() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const validRows = useMemo(() => parsedRows.filter(r => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter(r => r.errors.length > 0), [parsedRows]);
  const totalAmount = useMemo(() => validRows.reduce((sum, r) => sum + (Number(r.data.amount) || 0), 0), [validRows]);
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bill_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const processFile = useCallback((file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const {
        headers,
        rows
      } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.');
        return;
      }
      const keyMap: (string | null)[] = headers.map(mapHeaderToKey);
      const unmapped = headers.filter((_, i) => keyMap[i] === null);
      if (unmapped.length > 0 && keyMap.filter(Boolean).length === 0) {
        setParseError(`Could not match any columns. Please use the template format. Unrecognized headers: ${unmapped.join(', ')}`);
        return;
      }
      const parsed: ParsedRow[] = rows.map((values, idx) => {
        const data: Record<string, string> = {};
        keyMap.forEach((key, i) => {
          if (key && values[i] !== undefined) {
            data[key] = values[i];
          }
        });
        return {
          id: `row-${idx}`,
          data,
          errors: validateRow(data)
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const removeRow = (id: string) => {
    setParsedRows(prev => prev.filter(r => r.id !== id));
  };
  const handleImport = () => {
    // In a real app, this would POST to an API and create bills
    setStep('success');
  };
  const handleReset = () => {
    setStep('upload');
    setParsedRows([]);
    setFileName('');
    setParseError('');
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-4425" data-mp="2f4u9hcnufmpc">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4426" data-mp="2a7u3f1qzujy8">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-4427" data-mp="37nrima4bqyth">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-4428" data-mp="2b9gglksl9z75" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4429" data-mp="2g0q3weqo0qfv">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-4430" data-mp="229teews1cpd1">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-4431" data-mp="277d3krq9umjr">
            {t('pages.BulkBillUpload.bulk_upload_bills')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-4432" data-mp="2uyij15savctj">
            {t('pages.BulkBillUpload.upload_a_csv_file_to_add')}
          </p>
        </div>

        <div className="p-8" data-id="element-4433" data-mp="2ywg888m2lywx">
          {/* ========== UPLOAD STEP ========== */}
          {step === 'upload' && <div className="space-y-8" data-id="element-4434" data-mp="2pxuinti6ljmn">
              {/* Template download */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-center justify-between" data-id="element-4435" data-mp="2pzxewv9xg8zt">
                <div className="flex items-center" data-id="element-4436" data-mp="1eio1oqhe6d6y">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4" data-id="element-4437" data-mp="1ek8uaak5bm26">
                    <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-4438" data-mp="171j3m0946fs7" />
                  </div>
                  <div data-id="element-4439" data-mp="122fvg6xu7tg0">
                    <p className="text-sm font-medium text-gray-900" data-id="element-4440" data-mp="1fzuqpsaruvfw">
                      {t('pages.BulkBillUpload.download_template')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4441" data-mp="1b1t7969fou3w">
                      {t('pages.BulkBillUpload.use_this_csv_template_to_format')}
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4442" data-mp="x1ijic3f7vmy">
                  <DownloadIcon size={16} className="mr-2" data-id="element-4443" data-mp="3vrikjeuho7uh" />
                  {t('pages.BulkBillUpload.download_csv')}
                </button>
              </div>

              {/* Dropzone */}
              <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging ? 'border-[#E2231A] bg-[#E2231A]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-4444" data-mp="11z4p9wggk0z">
                <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-4445" data-mp="3j9f6yf0r3cz1" />

                <div className="flex flex-col items-center" data-id="element-4446" data-mp="34iy3yr8gwf01">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#E2231A]/10' : 'bg-gray-100'}`} data-id="element-4447" data-mp="34gd2w4fzu33c">
                    <UploadCloudIcon size={28} className={isDragging ? 'text-[#9F140F]' : 'text-gray-400'} data-id="element-4448" data-mp="2vijergsdufr9" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-4449" data-mp="1jzvr5tcxr1ap">
                    {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-4450" data-mp="sair29dbopkg">
                    {t('pages.BulkBillUpload.or_click_to_browse')}
                  </p>
                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-4451" data-mp="119w236h3hm7a">
                    {t('pages.BulkBillUpload.supported_csv')}
                  </span>
                </div>
              </div>

              {parseError && <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-4452" data-mp="djtfh5vy98g2">
                  <AlertCircleIcon size={18} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-4453" data-mp="1gupe1agr6ahu" />

                  <div data-id="element-4454" data-mp="1bvw23mh46rjs">
                    <p className="text-sm font-medium text-red-800" data-id="element-4455" data-mp="1pnj6yap4h8e6">
                      {t('pages.BulkBillUpload.upload_error')}
                    </p>
                    <p className="text-xs text-red-600 mt-0.5" data-id="element-4456" data-mp="1lmh83uz4aycq">{parseError}</p>
                  </div>
                </div>}

              {/* Field reference */}
              <div data-id="element-4457" data-mp="syblt3sf11as">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4458" data-mp="syurnm4sjz8g">
                  {t('pages.BulkBillUpload.expected_fields')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-4459" data-mp="11y86yvflji13">
                  {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-4460" data-mp="2ib6qdmenpsz8">
                      <span className="font-medium text-gray-800 mr-1" data-id="element-4461" data-mp="2lsknxh7lvvw9">
                        {col.label}
                      </span>
                      {col.required && <span className="text-red-400 text-[10px]" data-id="element-4462" data-mp="2bwzptu5nrfrs">*</span>}
                    </div>)}
                </div>
              </div>
            </div>}

          {/* ========== PREVIEW STEP ========== */}
          {step === 'preview' && <div className="space-y-6" data-id="element-4463" data-mp="39ioqdl925plo">
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-5 py-4" data-id="element-4464" data-mp="2uvc1bmk1l1qj">
                <div className="flex items-center space-x-6" data-id="element-4465" data-mp="2zsvh4qwe1mkl">
                  <div className="flex items-center text-sm" data-id="element-4466" data-mp="3njres4nizbzr">
                    <FileSpreadsheetIcon size={16} className="text-gray-400 mr-2" data-id="element-4467" data-mp="3ni6leicy5z3h" />

                    <span className="font-medium text-gray-900" data-id="element-4468" data-mp="23owpevsam9mn">
                      {fileName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm" data-id="element-4469" data-mp="1zli5vv55bb4g">
                    <span className="text-gray-600" data-id="element-4470" data-mp="1uorqzb52ulkm">
                      <span className="font-semibold text-gray-900" data-id="element-4471" data-mp="1polwln7fakyu">
                        {parsedRows.length}
                      </span>{' '}
                      {t('pages.BulkBillUpload.rows_total')}
                    </span>
                    <span className="text-red-600" data-id="element-4472" data-mp="2id19a3330r70">
                      <span className="font-semibold" data-id="element-4473" data-mp="2ifnf9yabqyn4">{validRows.length}</span>{' '}
                      {t('pages.BulkBillUpload.valid')}
                    </span>
                    {errorRows.length > 0 && <span className="text-red-600" data-id="element-4474" data-mp="362vkyhbo09po">
                        <span className="font-semibold" data-id="element-4475" data-mp="364gfw81w5uo2">
                          {errorRows.length}
                        </span>{' '}
                        {t('pages.BulkBillUpload.with_errors')}
                      </span>}
                  </div>
                </div>
                <button onClick={handleReset} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-4476" data-mp="3dn69or87zh29">
                  {t('pages.BulkBillUpload.upload_different_file')}
                </button>
              </div>

              {/* Total amount banner */}
              {validRows.length > 0 && <div className="bg-[#E2231A]/5 border border-[#E2231A]/20 rounded-lg px-5 py-4 flex items-center justify-between" data-id="element-4477" data-mp="etcv7ntgwcp">
                  <span className="text-sm text-gray-700" data-id="element-4478" data-mp="57czsv8e93u6">
                    {t('pages.BulkBillUpload.total_amount_across')}{' '}
                    <span className="font-semibold" data-id="element-4479" data-mp="f4r2zt5b3w4j">{validRows.length}</span>{' '}
                    {t('pages.BulkBillUpload.valid_bills')}
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-4480" data-mp="j6m4oyr5gbiu">
                    {t('pages.BillApprovalWizard.usd')}{' '}
                    {formatCurrency(totalAmount)}
                  </span>
                </div>}

              {/* Error rows */}
              {errorRows.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-4481" data-mp="2p153t9gd6xx3">
                  <div className="flex items-center mb-3" data-id="element-4482" data-mp="2k7ai60h788vd">
                    <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-4483" data-mp="2ywot3d0faycv" />
                    <span className="text-sm font-medium text-red-800" data-id="element-4484" data-mp="2tyna0s2zyz1e">
                      {errorRows.length} {t('pages.BulkBillUpload.row')}
                      {errorRows.length > 1 ? 's' : ''}{' '}
                      {t('pages.BulkBillUpload.with_validation_errors')}
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mb-3" data-id="element-4485" data-mp="1dl6por6bxlp0">
                    {t('pages.BulkBillUpload.these_rows_have_issues_and_will')}
                  </p>
                  <div className="space-y-2" data-id="element-4486" data-mp="zev9aviqtv8h">
                    {errorRows.map(row => <div key={row.id} className="flex items-center justify-between bg-white rounded-md border border-red-200 px-4 py-2.5" data-id="element-4487" data-mp="13g6yizxpgeg5">
                        <div className="flex-1 min-w-0" data-id="element-4488" data-mp="und6e1x5tkkv">
                          <span className="text-sm font-medium text-gray-900 mr-3" data-id="element-4489" data-mp="ulsbucaui1n0">
                            {row.data.billNumber || '(no bill number)'}
                          </span>
                          <span className="text-xs text-gray-500 mr-3" data-id="element-4490" data-mp="kr0ffbt01ktt">
                            {row.data.supplierName || '(no supplier)'}
                          </span>
                          <span className="text-xs text-red-600" data-id="element-4491" data-mp="bq2bsumh3fbr">
                            {row.errors.join('; ')}
                          </span>
                        </div>
                        <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors ml-3" data-id="element-4492" data-mp="263la46obv1y4">
                          <TrashIcon size={14} data-id="element-4493" data-mp="39p646lb7wnuz" />
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Preview table */}
              {validRows.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4494" data-mp="2vy0zv5wwn7eb">
                  <div className="overflow-x-auto" data-id="element-4495" data-mp="2qxn3wm8kvnwn">
                    <table className="w-full text-left" data-id="element-4496" data-mp="2qzzkow2lns84">
                      <thead data-id="element-4497" data-mp="2lzuzisqqy9bk">
                        <tr className="bg-gray-50 border-b border-gray-200" data-id="element-4498" data-mp="2lz3gsii9062p">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10" data-id="element-4499" data-mp="2hyjihblxuwez">
                            #
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4500" data-mp="g5lmx80s1pqc">
                            {t('pages.BillFinancingWizard.bill_number')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4501" data-mp="2319z1xoq8d0o">
                            {t('pages.BillFinancingWizard.supplier')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4502" data-mp="2bu3im7b7hykc">
                            {t('pages.BulkBillUpload.email')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-4503" data-mp="1k5tcx584k7er">
                            {t('pages.BillApprovalWizard.amount')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4504" data-mp="2zkhcm3dcq7hk">
                            {t('pages.BillForm.currency')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4505" data-mp="3609x3oh8vnt3">
                            {t('pages.BillDetail.due_date')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4506" data-mp="3fqcyod2fma31">
                            {t('pages.BulkBillUpload.description')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12" data-id="element-4507" data-mp="2n27athg304yz"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-4508" data-mp="2wzv5y8hlneho">
                        {validRows.map((row, idx) => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4509" data-mp="31yyazsxkxkpn" data-mpi={idx}>
                            <td className="px-4 py-3 text-xs text-gray-400" data-id="element-4510" data-mp="h87akhi8hu0z">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-4511" data-mp="21pyo9sv9mo5j">
                              {row.data.billNumber || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-4512" data-mp="1rzn8n927yqit">
                              {row.data.supplierName || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4513" data-mp="1i2r7joenh6oa">
                              {row.data.supplierEmail || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right font-mono" data-id="element-4514" data-mp="191t1l0knip32">
                              {row.data.amount ? formatCurrency(Number(row.data.amount)) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4515" data-mp="2ygr2sm2jw857">
                              {row.data.currency || getTenant().currency.code}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-4516" data-mp="wpqlidx497za">
                              {row.data.dueDate || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" data-id="element-4517" data-mp="muyr1iytz18q">
                              {row.data.description || '-'}
                            </td>
                            <td className="px-4 py-3" data-id="element-4518" data-mp="1fjdzfmngilb3">
                              <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors" data-id="element-4519" data-mp="1kgw0kqv8fw8j">
                                <TrashIcon size={14} data-id="element-4520" data-mp="1al2t3lwdesxm" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {parsedRows.length === 0 && <div className="text-center py-12 text-gray-500 text-sm" data-id="element-4521" data-mp="28d0sjniyo1tb">
                  {t('pages.BulkBillUpload.all_rows_have_been_removed_upload')}
                </div>}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-4522" data-mp="2ic02rajdk5if">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4523" data-mp="1um5nynfhw6vj">
                  {t('pages.BillApprovalWizard.cancel')}
                </button>
                <button onClick={handleImport} disabled={validRows.length === 0} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${validRows.length > 0 ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4524" data-mp="1ylwfwugrct1n">
                  {t('pages.BulkBillUpload.import')} {validRows.length}{' '}
                  {t('pages.BillApprovalWizard.bill')}
                  {validRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && <div className="text-center py-16" data-id="element-4525" data-mp="2386z723wythb">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-4526" data-mp="27a1yy2674ysz">
                <CheckCircleIcon size={32} className="text-green-600" data-id="element-4527" data-mp="2c8wpjqaenrnn" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-4528" data-mp="2h91absicj8jo">
                {t('pages.BulkBillUpload.bills_imported_successfully')}
              </h2>
              <p className="text-sm text-gray-500 mb-2" data-id="element-4529" data-mp="hxi3bjglsacn">
                {validRows.length} {t('pages.BillApprovalWizard.bill')}
                {validRows.length !== 1 ? 's have' : ' has'}{' '}
                {t('pages.BulkBillUpload.been_added_to_your_account')}
              </p>
              <p className="text-sm text-gray-700 font-medium mb-8" data-id="element-4530" data-mp="aixyituk5kk1">
                {t('pages.BulkBillUpload.total_usd')}{' '}
                {formatCurrency(totalAmount)}
              </p>
              <div className="flex justify-center gap-3" data-id="element-4531" data-mp="nmhu5zla39n">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-4532" data-mp="yad5izxa4q1m">
                  {t('pages.BulkBillUpload.upload_more')}
                </button>
                <button onClick={() => navigate('/bills')} className="px-5 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4533" data-mp="jdc9wlbywaxv">
                  {t('pages.BulkBillUpload.go_to_bills')}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}